import type { Property } from '~/core/entities/property';
import type {
	Accommodation,
	AggregateOffer,
	Apartment,
	House,
	ImageObject,
	Offer,
	OfferForLease,
	OfferForPurchase,
	Person,
	Product,
	QuantitativeValue,
	WithContext
} from 'schema-dts';
import { FeatureEnum, type PropertyTypeEnum } from '~/core/entities/CRMEnums';
import { PropertyPresenter } from './PropertyPresenter';

type AccommodationObject = Exclude<Accommodation, string>;
type AccommodationType = Extract<
	AccommodationObject['@type'],
	'Accommodation' | 'Apartment' | 'House'
>;

type ApartmentObject = Exclude<Apartment, string>;
type HouseObject = Exclude<House, string>;

export class PropertyJsonLdPresenter {
	static getProductJsonLd(
		property: Property,
		{
			websiteUrl,
			includeOffer = true
		}: { websiteUrl: string; includeOffer?: boolean }
	): WithContext<Product> {
		const accommodationJsonLd = this.#getAccommodationJsonLd(property, {
			websiteUrl
		});

		const propertyPageUrl = PropertyPresenter.getPropertyPageUrl(
			property,
			websiteUrl
		);

		return {
			'@type': 'Product',
			'@id': property.commercialId,
			'@context': 'https://schema.org',
			name: property.ad.title,
			description: property.ad.description,
			image: this.getPropertyJsonLdImageObject(property),
			url: propertyPageUrl,
			offers: includeOffer
				? this.getPropertyOfferJsonLd(property, { websiteUrl })
				: undefined,
			category: accommodationJsonLd
		};
	}

	static getPropertyOfferJsonLd(
		property: Property,
		{ websiteUrl }: { websiteUrl: string }
	): AggregateOffer | OfferForLease | OfferForPurchase | undefined {
		const companyLogo = this.#getPropertyCompanyJsonLdImageObject(property);

		const propertyBrokerJsonLd: Person = {
			'@type': 'Person',
			additionalType: 'RealEstateAgent',
			name: property.agent.name,
			memberOf: {
				'@type': 'Organization',
				additionalType: 'RealEstateAgent',
				'@id': property.company.id,
				name: property.company.name,
				logo: companyLogo,
				image: companyLogo
			}
		};

		const commonPropertyData: Omit<Offer, 'price' | '@type'> = {
			priceCurrency: 'BRL',
			itemOffered: this.getProductJsonLd(property, {
				websiteUrl,
				includeOffer: false
			}),
			url: PropertyPresenter.getPropertyPageUrl(property, websiteUrl),
			offeredBy: propertyBrokerJsonLd
		};

		const offers: (OfferForLease | OfferForPurchase)[] = [
			this.#getPropertyTransactionOffer({
				property,
				data: commonPropertyData,
				transactionType: 'sell'
			}),
			this.#getPropertyTransactionOffer({
				property,
				data: commonPropertyData,
				transactionType: 'rent'
			})
		].filter(property => property !== null);

		if (offers.length === 0) return undefined;
		if (offers.length === 1) return offers[0];

		return {
			'@type': 'AggregateOffer',
			priceCurrency: 'BRL',
			offers
		};
	}

	static #getPropertyTransactionOffer(options: {
		property: Property;
		data: Omit<OfferForPurchase, 'price' | '@type'>;
		transactionType: 'sell';
	}): OfferForPurchase | null;
	static #getPropertyTransactionOffer(options: {
		property: Property;
		data: Omit<OfferForLease, 'price' | '@type'>;
		transactionType: 'rent';
	}): OfferForLease | null;
	static #getPropertyTransactionOffer({
		property,
		data,
		transactionType
	}: {
		property: Property;
		data: Omit<OfferForPurchase | OfferForLease, 'price' | '@type'>;
		transactionType: 'sell' | 'rent';
	}): OfferForPurchase | OfferForLease | null {
		if (transactionType === 'sell') {
			if (!property.askingPrice) return null;

			return {
				...data,
				'@type': 'OfferForPurchase',
				price: property.askingPrice
			};
		}

		if (!property.rentPrice) return null;

		return {
			...data,
			'@type': 'OfferForLease',
			price: property.rentPrice
		};
	}

	static getPropertyJsonLdImageObject(
		property: Property
	): ImageObject | undefined {
		if (!property.thumbnailUrl) return undefined;

		return {
			'@type': 'ImageObject',
			url: property.thumbnailUrl
		};
	}

	static #getPropertyCompanyJsonLdImageObject({
		company
	}: Property): ImageObject | undefined {
		const url = company.largeLogo || company.smallLogo;

		return url ? { '@type': 'ImageObject', url } : undefined;
	}

	static #getAccommodationJsonLd(
		property: Property,
		{ websiteUrl }: { websiteUrl: string }
	): AccommodationObject | ApartmentObject | HouseObject {
		const propertyFeatures = Object.values(property.featuresByCategory)
			.flat()
			.map(feature => feature.identifier);

		const launchDate = property.launchDate || undefined;

		const yearBuilt: number | undefined = launchDate?.match(/\d{4}-\d{2}-\d{2}/)
			? +(property.launchDate.split('-')[0] || 0) || undefined
			: undefined;

		const potentialAction: AccommodationObject['potentialAction'] =
			property.askingPrice || property.rentPrice
				? {
						'@type': property.askingPrice ? 'BuyAction' : 'RentAction',
						price: property.askingPrice || property.rentPrice || undefined,
						priceCurrency: 'BRL'
					}
				: undefined;

		const floorSize: QuantitativeValue | undefined = property.totalArea
			? {
					'@type': 'QuantitativeValue',
					value: property.totalArea,
					unitCode: 'NTK'
				}
			: undefined;

		const propertyPageUrl = PropertyPresenter.getPropertyPageUrl(
			property,
			websiteUrl
		);

		const commonData: Omit<AccommodationObject, '@type'> = {
			name: property.ad.title,
			description: property.ad.description,
			url: propertyPageUrl,
			address: this.#getPropertyAddressText(property),
			floorSize,
			containedInPlace: property.condoName || property.building || undefined,
			identifier: property.commercialId,
			mainEntityOfPage: propertyPageUrl,
			numberOfBathroomsTotal: property.bathrooms || undefined,
			numberOfBedrooms: property.bedrooms || undefined,
			photo: this.getPropertyJsonLdImageObject(property),
			petsAllowed: propertyFeatures.includes(FeatureEnum.PETS_ALLOWED),
			yearBuilt,
			potentialAction
		};

		return {
			...commonData,
			'@type': this.#getAccommodationType(property.propertyType.identifier)
		};
	}

	static #getPropertyAddressText(property: Property): string {
		return `${property.region}, ${property.city} - ${property.state}`;
	}

	static #getAccommodationType(
		propertyType: PropertyTypeEnum
	): AccommodationType {
		switch (propertyType) {
			case 'apartment':
				return 'Apartment';
			case 'house':
				return 'House';
			default:
				return 'Accommodation';
		}
	}
}
